import { Button, Icon, TextButton, Trigger, isSuccess, useLaravelCrudEntity, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { contactPhoneService } from "../../shared/api/contactPhoneService";
import { contactService } from "../../shared/api/contactService";
import { IContactPhoneFormState, IContactUpdateResource } from "../../shared/types";
import {
	DrawerFormButtonContainer,
	MarkAsPrimaryButton,
	PrimaryLabel,
	ViewPageSection,
	ViewPageSectionBody,
	ViewPageSectionHeader,
	ViewPageSectionItem,
	ViewPageSectionListItem,
} from "../../shared/view";
import { ValueWrapper } from "../../shared/view/ViewComponents";
import { ContactPhoneDrawer } from "./ContactPhoneDrawer";

const StyledViewPageSection = styled(ViewPageSection)`
	grid-area: phone;
`;

export const ContactViewPhoneNumberSection: FC<{ contact: IContactUpdateResource }> = ({ contact }) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const crudOperations = useLaravelCrudEntity(contactPhoneService, {
		entityName: t("Contact phone"),
		messages: {
			create: t("Contact phone was saved."),
			update: t("Contact phone was saved."),
			delete: t("Contact phone was deleted."),
		},
	});
	const emptyContactPhone: IContactPhoneFormState = {
		type: "workLandline",
		phoneNumber: "",
		isPrimary: false,
	};
	const onClosed = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: contactService.listKey });
		queryClient.invalidateQueries({ queryKey: contactService.showKey(contact.id) });
	}, [contact.id, queryClient]);

	return (
		<StyledViewPageSection>
			<ViewPageSectionHeader>{t("Phone numbers")}</ViewPageSectionHeader>
			<ViewPageSectionBody>
				{contact.contactPhones.map((x) => (
					<Trigger
						key={x.id}
						render={(props) => (
							<ContactPhoneDrawer
								open={props.open}
								onClose={props.handleClose}
								title={t("Edit contact phone")}
								initialValues={x}
								onClosed={onClosed}
								onSubmit={(values) =>
									crudOperations.updateEntity(x.id, {
										...values,
										contactId: contact.id,
									})
								}
							>
								{!x.isPrimary && (
									<DrawerFormButtonContainer>
										<MarkAsPrimaryButton
											entityId={x.id}
											markAsPrimary={contactPhoneService.markAsPrimary}
											onSubmit={props.handleClose}
										/>
										<Button
											type="button"
											color="danger"
											onClick={async () => {
												const res = await contactService.deletePhoneNumber(contact.id, x.id);
												if (isSuccess(res)) {
													showToast({
														body: t("Phone number was deleted."),
														title: t("Company"),
														type: "success",
													});
													props.handleClose();
												}
											}}
										>
											{t("Delete phone number")}
										</Button>
									</DrawerFormButtonContainer>
								)}
							</ContactPhoneDrawer>
						)}
					>
						{(props) => (
							<ViewPageSectionListItem {...props}>
								<ValueWrapper>
									<span>{x.phoneNumber}</span>
									{x.isPrimary && <PrimaryLabel />}
								</ValueWrapper>
							</ViewPageSectionListItem>
						)}
					</Trigger>
				))}
				<ViewPageSectionItem>
					<Trigger
						render={(props) => (
							<ContactPhoneDrawer
								open={props.open}
								onClose={props.handleClose}
								title={t("Add phone number")}
								initialValues={emptyContactPhone}
								onClosed={onClosed}
								onSubmit={(values) =>
									crudOperations.createEntity({
										...values,
										contactId: contact.id,
									})
								}
							/>
						)}
					>
						{(props) => (
							<TextButton {...props} color="primary" prefix={<Icon $icon="plus" />}>
								{t("Add phone number")}
							</TextButton>
						)}
					</Trigger>
				</ViewPageSectionItem>
			</ViewPageSectionBody>
		</StyledViewPageSection>
	);
};
